import moment, { Moment } from 'moment-timezone';

import { Dayjs, dayjs } from './dayjs';

export const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * @param   date
 * @param   format
 * @returns M/D/YYYY
 */
export function formatDateForDisplay(date: string | number | Date | Moment, format = 'M/D/YYYY') {
	if (typeof date === 'string') {
		return moment(date, 'YYYY-MM-DD').format(format);
	}

	return moment(date).format(format);
}

export function formatDateForDisplayForRates(timestamp: string, timezone: string = DEFAULT_TIMEZONE) {
	const dateFromUnix = moment(timestamp, 'x').tz(timezone);
	return moment(dateFromUnix).format('MM/DD/YYYY hh:mm a z');
}

/**
 * Use this for timestamps returned from GraphQL
 * @param   timestamp   Unix ms (e.g. 1663351671000)
 * @param   timezone
 * @param   format
 * @returns M/D/YYYY
 */
export function convertUnixMsToDateInTimezone(
	timestamp: string,
	timezone: string = DEFAULT_TIMEZONE,
	format: string = 'M/D/YYYY',
) {
	const convertedTime = moment(timestamp, 'x').tz(timezone);
	return formatDateForDisplay(convertedTime, format);
}

export function convertUnixMsToDate(timestamp: string, format: string = 'M/D/YYYY') {
	return dayjs.utc(timestamp, 'x').format(format);
}

export function buildDateFilters(op: string, value: string) {
	const timezone = 'UTC'; // Default since the invoices span multiple facilities

	const startOfDay = moment.tz(value, 'YYYY-MM-DD', timezone).startOf('day').format();
	const endOfDay = moment.tz(value, 'YYYY-MM-DD', timezone).endOf('day').format();

	switch (op) {
		case 'EQ':
		case 'NE': {
			return [
				{
					op: op === 'EQ' ? 'GTE' : 'LTE',
					value: startOfDay,
				},
				{
					op: op === 'EQ' ? 'LTE' : 'GTE',
					value: endOfDay,
				},
			];
		}
		case 'GT':
		case 'LTE':
			return [
				{
					op,
					value: endOfDay,
				},
			];
		case 'GTE':
		case 'LT':
			return [
				{
					op,
					value: startOfDay,
				},
			];
		default:
			return [
				{
					op,
					value,
				},
			];
	}
}

export const formatDate = (date) => {
	if (date) {
		if (typeof date === 'string') {
			return dayjs(date, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY');
		}

		if (date instanceof Date) {
			return dayjs(date).format('MM/DD/YYYY');
		}
	}

	return '';
};

export const formatDateOnly = (date) => (date ? dayjs.utc(date).format('MM/DD/YYYY') : '');

export const formatDateWithTime = (date, timezone = DEFAULT_TIMEZONE) => {
	// Database saves dates in UTC/GMT
	dayjs.tz.setDefault('GMT');

	let formattedDate = '';

	if (date) {
		formattedDate = dayjs(date).tz(timezone).format('YYYY-MM-DD HH:mm:ss');
	}

	dayjs.tz.setDefault();

	return formattedDate;
};

export const convertToDate = (date) => (date ? dayjs(date).toDate() : null);

export const formatDateForDatePicker = (date) => {
	if (date) {
		if (typeof date === 'string') {
			return date.split('T')[0];
		}

		if (date instanceof Date) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');

			return `${year}-${month}-${day}`;
		}
	}

	return '';
};

export const getDayjsFromDeviceDate = (date: Dayjs | string | null) => {
	if (!date) return null;
	return typeof date === 'string' ? dayjs.utc(date, 'MM/DD/YYYY') : date;
};
